import * as React from 'react';

import { cn } from '@april9au/react-common/utils';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';

import { SectionHeader } from './section-header';

export type GalleryImage = {
  src: string;
  alt?: string;
  caption?: string;
};

/**
 * Grid of images that open in a full-screen lightbox with prev/next and
 * keyboard navigation. Reusable for property photo sets, area/lifestyle
 * galleries, or before/after shots — anywhere a single hero or content image
 * isn't enough.
 */
export function ImageGallery({
  id,
  eyebrow,
  heading,
  description,
  images,
  className,
}: {
  id?: string;
  eyebrow?: string;
  heading?: string;
  description?: string;
  images: Array<GalleryImage>;
  className?: string;
}) {
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const active = activeIndex !== null ? images[activeIndex] : null;

  const showPrev = React.useCallback(() => {
    setActiveIndex((current) => (current === null ? current : (current - 1 + images.length) % images.length));
  }, [images.length]);

  const showNext = React.useCallback(() => {
    setActiveIndex((current) => (current === null ? current : (current + 1) % images.length));
  }, [images.length]);

  React.useEffect(() => {
    if (activeIndex === null) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'ArrowLeft') showPrev();
      if (event.key === 'ArrowRight') showNext();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeIndex, showPrev, showNext]);

  return (
    <section id={id} className={cn('section', className)}>
      <div className="container">
        {heading ? <SectionHeader eyebrow={eyebrow} heading={heading} description={description} className="mb-12" /> : null}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {images.map((image, index) => (
            <button
              key={image.src + index}
              type="button"
              onClick={() => setActiveIndex(index)}
              className="group relative aspect-4/3 w-full overflow-hidden rounded-md"
            >
              <img
                src={image.src}
                alt={image.alt ?? ''}
                className="size-full object-cover transition-transform duration-300 ease-smooth group-hover:scale-105"
              />
              <span className="bg-navy/50 absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <Icon name="heroicons/magnifying-glass-micro" className="text-gold size-6" />
              </span>
            </button>
          ))}
        </div>

        <Dialog open={active !== null} onOpenChange={(open) => !open && setActiveIndex(null)}>
          <DialogContent className="border-none bg-transparent p-0 shadow-none sm:max-w-3xl">
            <DialogTitle className="sr-only">{active?.alt || active?.caption || 'Gallery image'}</DialogTitle>

            {active ? (
              <div className="relative">
                <img src={active.src} alt={active.alt ?? ''} className="max-h-[80vh] w-full rounded-md object-contain" />

                {images.length > 1 ? (
                  <>
                    <button
                      type="button"
                      onClick={showPrev}
                      aria-label="Previous image"
                      className="bg-navy/70 text-gold hover:bg-navy/90 absolute top-1/2 left-2 inline-flex size-9 -translate-y-1/2 items-center justify-center rounded-full transition-colors"
                    >
                      <Icon name="heroicons/chevron-left-micro" className="size-5" />
                    </button>
                    <button
                      type="button"
                      onClick={showNext}
                      aria-label="Next image"
                      className="bg-navy/70 text-gold hover:bg-navy/90 absolute top-1/2 right-2 inline-flex size-9 -translate-y-1/2 items-center justify-center rounded-full transition-colors"
                    >
                      <Icon name="heroicons/chevron-right-micro" className="size-5" />
                    </button>
                  </>
                ) : null}
              </div>
            ) : null}

            {active?.caption ? <p className="text-caption text-center">{active.caption}</p> : null}
            {images.length > 1 ? (
              <p className="text-caption text-center">
                {(activeIndex ?? 0) + 1} / {images.length}
              </p>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}
